export const APPOINTMENT_NOTIFICATION_TYPES = new Set([
  'appointment_booked',
  'appointment_confirmed',
  'appointment_updated',
  'appointment_reassignment',
  'appointment_rescheduled',
  'appointment_cancelled',
  'appointment_completed',
  'appointment_reminder',
  'payment_success',
  'payment_failed',
  // Synthetic booking-feed aliases (buildNotificationFromBooking)
  'booked',
  'cancelled',
  'completed',
])

export const FAVORITE_NOTIFICATION_TYPES = new Set([
  'favorite_added',
  'favorite_removed',
  'favorite',
])

export const PROFILE_NOTIFICATION_TYPES = new Set([
  'profile_created',
  'profile_updated',
  'profile',
])

export const SUPPORT_NOTIFICATION_TYPES = new Set([
  'support_resolved',
  'support',
  'support_ticket',
  'support_updated',
])

const SYNTHETIC_TYPE_MAP = {
  booked: 'appointment_booked',
  cancelled: 'appointment_cancelled',
  completed: 'appointment_completed',
}

export const normalizeNotificationType = (type) => {
  if (!type) return null
  return SYNTHETIC_TYPE_MAP[type] || type
}

export const getAppointmentTabForNotificationType = (type) => {
  const normalized = normalizeNotificationType(type)
  switch (normalized) {
    case 'appointment_cancelled':
    case 'cancelled':
      return 'cancelled'
    case 'appointment_completed':
    case 'completed':
      return 'completed'
    case 'appointment_reminder':
    case 'appointment_booked':
    case 'appointment_confirmed':
    case 'appointment_updated':
    case 'appointment_reassignment':
    case 'appointment_rescheduled':
    case 'payment_success':
    case 'payment_failed':
    case 'booked':
    default:
      return 'upcoming'
  }
}

export const isAppointmentNotificationType = (type) =>
  APPOINTMENT_NOTIFICATION_TYPES.has(type) ||
  APPOINTMENT_NOTIFICATION_TYPES.has(normalizeNotificationType(type))

export const getNotificationDestination = (type) => {
  const normalized = normalizeNotificationType(type)
  if (!normalized) return null
  if (
    APPOINTMENT_NOTIFICATION_TYPES.has(normalized) ||
    APPOINTMENT_NOTIFICATION_TYPES.has(type)
  ) {
    return 'bookings'
  }
  if (
    FAVORITE_NOTIFICATION_TYPES.has(normalized) ||
    FAVORITE_NOTIFICATION_TYPES.has(type)
  ) {
    return 'favorites'
  }
  if (
    PROFILE_NOTIFICATION_TYPES.has(normalized) ||
    PROFILE_NOTIFICATION_TYPES.has(type)
  ) {
    return 'profile'
  }
  if (
    SUPPORT_NOTIFICATION_TYPES.has(normalized) ||
    SUPPORT_NOTIFICATION_TYPES.has(type)
  ) {
    return 'support'
  }
  return 'other'
}

export const getNotificationAppointmentId = (notification) => {
  if (!notification) return null
  return (
    notification.relatedId ||
    notification.bookingId ||
    notification.related_id ||
    notification.data?.relatedId ||
    notification.data?.related_id ||
    notification.data?.appointmentId ||
    notification.data?.appointment_id ||
    notification.data?.bookingId ||
    null
  )
}

export const buildOpenAppointmentPayload = (notification) => {
  const appointmentId = getNotificationAppointmentId(notification)
  if (!appointmentId) return null
  const notificationType = normalizeNotificationType(
    notification.rawType || notification.type,
  )
  return {
    appointmentId: String(appointmentId),
    bookingFilter: getAppointmentTabForNotificationType(notificationType),
    openDetails: true,
  }
}

/** Dispatch the correct UI action for a notification click (after mark-read). */
export const openNotificationDestination = (notification, { setBookingFilter } = {}) => {
  const rawType = notification?.rawType || notification?.type
  const destination = getNotificationDestination(rawType)

  if (destination === 'bookings') {
    const payload = buildOpenAppointmentPayload(notification)
    if (!payload?.appointmentId) return false
    if (typeof setBookingFilter === 'function') {
      setBookingFilter(payload.bookingFilter)
    }
    try {
      sessionStorage.setItem('openAppointmentDetails', JSON.stringify(payload))
    } catch (_) {
      // no-op
    }
    window.dispatchEvent(new CustomEvent('openBookingsSidebar'))
    return true
  }

  if (destination === 'favorites') {
    window.dispatchEvent(new CustomEvent('openFavoritesSidebar'))
    return true
  }

  if (destination === 'support') {
    window.dispatchEvent(new CustomEvent('openSupportFromNotification'))
    return true
  }

  if (destination === 'profile') {
    window.dispatchEvent(new CustomEvent('openProfileFromNotification'))
    return true
  }

  // Unknown / other — no navigation beyond mark-read
  return false
}
